import { isEqual } from 'lodash'
import type { IStateItem } from '~/types/models'
import type { TMSService } from '~/types/msService/models'
import { assertIsNotUndefined } from '~/types/models'

/**
 * ⚠ THESE IDS ARE THE SERVER'S `itemavailabilitystate_sn` VALUES, and the server's
 * own `id` column is `generated always as (itemavailabilitystate_sn::text)`. So a
 * member here must be the decimal string of the next free `_sn`, and a new one is
 * useless until the server seeds the matching row —
 * `models/item_availability_state.py`. See ItemAvailabilityStatus.test.ts, which
 * pins both the shape and the descrs the server owes us.
 */
export enum ItemAvailabilityIds {
  NOT_SET = '0',
  NOT_AVAILABLE = '1',
  OPTIONAL_NOT_INCLUDED = '2',
  OPTIONAL_INCLUDED = '3',
  MANDATORY = '4',
  OPTIONAL_SWAPPED_OUT = '5',
  OPTIONAL_REMOVED = '6',
}

export function getItemAvailabilityStates(): IStateItem[] {
  return [
    {
      id: ItemAvailabilityIds.NOT_SET,
      descr: '',
    },
    {
      id: ItemAvailabilityIds.NOT_AVAILABLE,
      descr: 'Not Available',
    },
    {
      id: ItemAvailabilityIds.OPTIONAL_NOT_INCLUDED,
      descr: 'Optional',
    },
    {
      id: ItemAvailabilityIds.OPTIONAL_INCLUDED,
      descr: 'Optional\nIncluded',
    },
    // ⚠ NOT blank. It read '' until 2026-08-27, which left the Availability
    // cell empty on every mandatory kit item -- indistinguishable from a row
    // that carries no availability state at all (a kit, or a stand-alone item).
    {
      id: ItemAvailabilityIds.MANDATORY,
      descr: 'Mandatory',
    },
    // The user had this one and then swapped it for another alternative from the
    // same Set. 'Optional' stays on line 1 so the states read as one family in
    // the 150px Inclusion column -- line 1 is whether the item was ever a
    // choice, line 2 is what the configuration did with it.
    {
      id: ItemAvailabilityIds.OPTIONAL_SWAPPED_OUT,
      descr: 'Optional\nSwapped Out',
    },
    // The user had this one and took it out; nothing replaced it.
    {
      id: ItemAvailabilityIds.OPTIONAL_REMOVED,
      descr: 'Optional\nRemoved',
    },
  ]
}

export function getItemAvailabilityStateById(id: string): IStateItem {
  const state = getItemAvailabilityStates().find((item) => isEqual(item.id, id))
  assertIsNotUndefined(state)
  return state
}

/**
 * Is this msService absent from the live configuration?
 *
 * Four ways that happens, and they are the same fact wearing different hats:
 *
 *   NOT_AVAILABLE          the supplier does not offer it
 *   OPTIONAL_NOT_INCLUDED  the user declined it (legacy rows only -- unchosen
 *                          options are no longer persisted)
 *   OPTIONAL_SWAPPED_OUT   the user had it and swapped it for another
 *                          alternative from the same Set
 *   OPTIONAL_REMOVED       the user had it and took it out; nothing replaced it
 *
 * ⚠ THIS PREDICATE IS THE SINGLE GATE ON ~30 CALL SITES -- the msServices card's
 * row filter, the kit amount totals in `processedItems`, `isCountableMSService`,
 * the numLocations / recursPaidDescr / all-the-same-term roll-ups, and the
 * similarity grouping. It also zeroes the row's quantity
 * (`deriveMSServiceQuantity`), which is why a hidden row contributes no MRC:
 * mrcAmount is quantity x rate. Get this wrong and a Set swap on a $60 item for
 * a $30 one moves a kit's displayed MRC from $239 to $269 rather than $209,
 * because the replaced row is still drawn AND still summed. Found by doing the
 * swap in the running app.
 *
 * ⚠ NRC, one-time and TCV do NOT go through quantity, so they are not zeroed by
 * being hidden: nrcAmount is `1 x unitNrcAmount` whenever
 * `doMultiplyNrcByQuantity` is false, oneTimeAmount is stored outright, and
 * tcvAmount collapses to oneTimeAmount once mrcAmount is zero. Excluding those
 * is the caller's job, not this predicate's.
 *
 * 🔑 THE SERVER MIRRORS THIS IN ONE COLUMN, and that is now true by
 * construction rather than by coincidence. `qualifies_to_be_hidden` is
 * generated over `itemavailabilitystate_sn` alone, so every id this function
 * tests is an id that column can test too -- which is precisely why the
 * superseded marker was moved off msService.status and onto an availability
 * state. While it was a status the two could only ever agree in effect, through
 * a parallel status clause on the server kept in step by hand.
 *
 * The name reads true for all four: they all surface through the same "Hidden"
 * row filter, and the Inclusion column that filter turns on says which.
 */
export function doesQualifyToBeHidden(msService: TMSService) {
  const availabilityId = msService.itemAvailabilityState?.id
  return (
    availabilityId === ItemAvailabilityIds.NOT_AVAILABLE ||
    availabilityId === ItemAvailabilityIds.OPTIONAL_NOT_INCLUDED ||
    availabilityId === ItemAvailabilityIds.OPTIONAL_SWAPPED_OUT ||
    availabilityId === ItemAvailabilityIds.OPTIONAL_REMOVED
  )
}
